import { createSelector } from 'reselect';
import flatten from 'lodash/flatten';
import get from 'lodash/get';
import Fuse from 'fuse.js';
import cloneDeep from 'lodash/cloneDeep';
import { baseModuleItemsFuseOptions } from '../constants';

export const getBaseModuleItemsList = state => state.baseModuleItems.itemsList;

export const getCurrentLang = state => state.baseModule.lang;

export const getDefaultLang = state => state.baseModule.langs.find(lng => lng.default);

export const itemListFilterSelectableCategory = (state) => {
  const moduleCategories = state.baseModule.currentModule ? state.baseModule.currentModule.categories : [];
  const treverse = function treverse(categories, acc = []) {
    categories.forEach((ctg) => {
      if (ctg.children && !ctg.children.length) {
        if (!acc.some(item => item.id === ctg.id)) acc.push(ctg);
      } else {
        treverse(ctg.children.map((i) => {
          // eslint-disable-next-line
          i.parent = ctg.title;
          return i;
        }), acc);
      }
    });
    return acc;
  };

  return treverse(moduleCategories);
};

const getNameFilter = state => state.baseModuleItems.filterByName;

const getCategoryFilter = state => state.baseModuleItems.filterByCategory;

export const getFilteredBaseModuleItemsList = createSelector(
  [getBaseModuleItemsList, getCurrentLang, getNameFilter, getCategoryFilter],
  (itemsList, currentLang, filterByName, filterByCategory) => {
    let filteredList = itemsList.slice();

    if (filterByName && filterByName.length) {
      const fuse = new Fuse(filteredList, baseModuleItemsFuseOptions);
      filteredList = fuse.search(filterByName);
    }

    if (filterByCategory && filterByCategory.id) {
      filteredList = filteredList.filter((item) => {
        const categories = item.categories.map(c => c.id);
        return categories.includes(filterByCategory.id);
      });
    }

    return filteredList;
  },
);

export const getFilteredModuleList = (state) => {
  const { modulesList, lang } = state.baseModule;
  return modulesList.filter(mod => mod.language === lang);
};

export const getDefaultModuleList = (state) => {
  const { modulesList } = state.baseModule;
  const defaultLang = getDefaultLang(state);
  return modulesList.filter(mod => mod.language === defaultLang.code);
};

const getCurrentModule = state => state.baseModule.currentModule;

const getModulesList = state => state.baseModule.modulesList;

export const getCurrentModuleFormInputs = createSelector([getCurrentModule, getModulesList],
  (currentModule, modulesList) => {
    if (!currentModule) return null;
    const langModules = modulesList.filter(module => module.internal_name === currentModule.internal_name);
    const initialValues = {};

    initialValues.id = {};
    initialValues.internalName = currentModule.internal_name;
    initialValues.fields = currentModule.fields.map(module => ({
      id: {},
      internalName: module.internal_name,
      label: {},
      type: module.type,
    }));
    initialValues.categories = cloneDeep(currentModule.categories);

    const treverseCategories = (formCategories, moduleCategories, lang) =>
      formCategories.map((item, index) => {
        const categoryField = { ...item };
        if (typeof categoryField.title === 'string') categoryField.title = {};

        if (!moduleCategories[index]) {
          return categoryField;
        }

        categoryField.title[lang] = moduleCategories[index].title;

        if (categoryField.image) {
          categoryField.image = {
            ...categoryField.image,
            alt: categoryField.image_alt,
            tooltip: categoryField.image_tooltip,
            hyperlink: categoryField.image_hyperlink,
          };
        }

        if (typeof categoryField.id !== 'object') {
          categoryField.id = {};
        }
        categoryField.id[lang] = moduleCategories[index].id;

        let categories = [];
        const children = get(categoryField, 'children.categories') || categoryField.children;
        if (children && children.length) {
          categories = treverseCategories(children, moduleCategories[index].children, lang);
        }

        categoryField.children = { categories };

        return categoryField;
      });

    langModules.forEach((module) => {
      initialValues.id[module.language] = module.id;
      initialValues[`label_${module.language}`] = module.label;

      initialValues.fields = initialValues.fields.map((item) => {
        const langField = module.fields.find(i => i.internal_name === item.internalName && i.type === item.type);
        const fieldItem = { ...item };
        fieldItem.label[module.language] = get(langField, 'label');
        fieldItem.id[module.language] = get(langField, 'id');

        return fieldItem;
      });
      initialValues.categories = initialValues.categories.map((item, index) => {
        const categoryField = { ...item };

        if (categoryField.image) {
          categoryField.image = {
            ...categoryField.image,
            alt: categoryField.image_alt,
            tooltip: categoryField.image_tooltip,
            hyperlink: categoryField.image_hyperlink,
          };
        }

        if (typeof categoryField.title === 'string') {
          categoryField.title = {};
        }
        categoryField.title[module.language] = get(module, `categories.${index}.title`);

        if (typeof categoryField.id !== 'object') {
          categoryField.id = {};
        }
        categoryField.id[module.language] = get(module, `categories.${index}.id`);

        let categories = [];
        const children = get(categoryField, 'children.categories') || categoryField.children;

        if (children && children.length) {
          categories = treverseCategories(children,
            module.categories[index] ? module.categories[index].children : [],
            module.language,
          );
        }

        categoryField.children = { categories };

        return categoryField;
      });
    });

    return initialValues;
  });

const getDetailFilters = state => state.baseModuleItems.customDetailFilters;

export const getFilterLabels = createSelector([getDetailFilters],
  (detailFilter) => {
    const normalizedFilters = {};
    const filters = flatten(detailFilter.map(filter => filter.filters));
    filters.forEach((filter) => {
      normalizedFilters[filter.id] = filter.label;
    });
    return normalizedFilters;
  },
);

export const baseModuleFetchData = state => (
  state.baseModule.fetchingLangs ||
  state.baseModule.fetchingFieldTypes ||
  state.baseModule.fetchingModulesList ||
  state.baseModule.fetchingUpdateModule ||
  state.baseModule.fetchingDeleteModule
);

export const baseModuleItemsFetchData = state => (
  state.baseModuleItems.fetchingItemsList ||
  state.baseModuleItems.fetchItemSave ||
  state.baseModuleItems.fetchItemDelete
);



// WEBPACK FOOTER //
// ./src/selectors/index.js